// src/services/chatPreferencesService.ts
import { logger } from '../utils/logger';
// Service for managing chat preferences (mute, archive)

import { supabase } from './supabase';

// ============================================================================
// Mute Functions
// ============================================================================

/**
 * Mute a 1-on-1 chat
 */
export async function muteChat(matchId: string): Promise<{ success: boolean; error?: any }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const { error } = await supabase
      .from('muted_chats')
      .insert({ user_id: user.id, match_id: matchId });

    if (error) {
      logger.error('Error muting chat:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error in muteChat:', error);
    return { success: false, error };
  }
}

/**
 * Unmute a 1-on-1 chat
 */
export async function unmuteChat(matchId: string): Promise<{ success: boolean; error?: any }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const { error } = await supabase
      .from('muted_chats')
      .delete()
      .eq('user_id', user.id)
      .eq('match_id', matchId);

    if (error) {
      logger.error('Error unmuting chat:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error in unmuteChat:', error);
    return { success: false, error };
  }
}

/**
 * Check if a chat is muted
 */
export async function isChatMuted(matchId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('muted_chats')
      .select('id')
      .eq('user_id', user.id)
      .eq('match_id', matchId)
      .single();

    if (error && error.code !== 'PGRST116') {
      logger.error('Error checking if chat is muted:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    logger.error('Error in isChatMuted:', error);
    return false;
  }
}

/**
 * Mute a group chat
 */
export async function muteGroup(groupId: string): Promise<{ success: boolean; error?: any }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const { error } = await supabase
      .from('muted_groups')
      .insert({ user_id: user.id, group_id: groupId });

    if (error) {
      logger.error('Error muting group:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error in muteGroup:', error);
    return { success: false, error };
  }
}

/**
 * Unmute a group chat
 */
export async function unmuteGroup(groupId: string): Promise<{ success: boolean; error?: any }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const { error } = await supabase
      .from('muted_groups')
      .delete()
      .eq('user_id', user.id)
      .eq('group_id', groupId);

    if (error) {
      logger.error('Error unmuting group:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error in unmuteGroup:', error);
    return { success: false, error };
  }
}

/**
 * Check if a group is muted
 */
export async function isGroupMuted(groupId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('muted_groups')
      .select('id')
      .eq('user_id', user.id)
      .eq('group_id', groupId)
      .single();

    if (error && error.code !== 'PGRST116') {
      logger.error('Error checking if group is muted:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    logger.error('Error in isGroupMuted:', error);
    return false;
  }
}

/**
 * Get all muted chat IDs for current user
 */
export async function getMutedChats(): Promise<string[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('muted_chats')
      .select('match_id')
      .eq('user_id', user.id);

    if (error) {
      logger.error('Error fetching muted chats:', error);
      return [];
    }

    return data.map(item => item.match_id);
  } catch (error) {
    logger.error('Error in getMutedChats:', error);
    return [];
  }
}

/**
 * Get all muted group IDs for current user
 */
export async function getMutedGroups(): Promise<string[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('muted_groups')
      .select('group_id')
      .eq('user_id', user.id);

    if (error) {
      logger.error('Error fetching muted groups:', error);
      return [];
    }

    return data.map(item => item.group_id);
  } catch (error) {
    logger.error('Error in getMutedGroups:', error);
    return [];
  }
}

// ============================================================================
// Archive Functions
// ============================================================================

/**
 * Archive a 1-on-1 chat
 */
export async function archiveChat(matchId: string): Promise<{ success: boolean; error?: any }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const { error } = await supabase
      .from('archived_chats')
      .insert({ user_id: user.id, match_id: matchId });

    if (error) {
      logger.error('Error archiving chat:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error in archiveChat:', error);
    return { success: false, error };
  }
}

/**
 * Unarchive a 1-on-1 chat
 */
export async function unarchiveChat(matchId: string): Promise<{ success: boolean; error?: any }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const { error } = await supabase
      .from('archived_chats')
      .delete()
      .eq('user_id', user.id)
      .eq('match_id', matchId);

    if (error) {
      logger.error('Error unarchiving chat:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error in unarchiveChat:', error);
    return { success: false, error };
  }
}

/**
 * Check if a chat is archived
 */
export async function isChatArchived(matchId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('archived_chats')
      .select('id')
      .eq('user_id', user.id)
      .eq('match_id', matchId)
      .single();

    if (error && error.code !== 'PGRST116') {
      logger.error('Error checking if chat is archived:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    logger.error('Error in isChatArchived:', error);
    return false;
  }
}

/**
 * Archive a group chat
 */
export async function archiveGroup(groupId: string): Promise<{ success: boolean; error?: any }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const { error } = await supabase
      .from('archived_groups')
      .insert({ user_id: user.id, group_id: groupId });

    if (error) {
      logger.error('Error archiving group:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error in archiveGroup:', error);
    return { success: false, error };
  }
}

/**
 * Unarchive a group chat
 */
export async function unarchiveGroup(groupId: string): Promise<{ success: boolean; error?: any }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const { error } = await supabase
      .from('archived_groups')
      .delete()
      .eq('user_id', user.id)
      .eq('group_id', groupId);

    if (error) {
      logger.error('Error unarchiving group:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error in unarchiveGroup:', error);
    return { success: false, error };
  }
}

/**
 * Check if a group is archived
 */
export async function isGroupArchived(groupId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('archived_groups')
      .select('id')
      .eq('user_id', user.id)
      .eq('group_id', groupId)
      .single();

    if (error && error.code !== 'PGRST116') {
      logger.error('Error checking if group is archived:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    logger.error('Error in isGroupArchived:', error);
    return false;
  }
}

/**
 * Get all archived chat IDs for current user
 */
export async function getArchivedChats(): Promise<string[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('archived_chats')
      .select('match_id')
      .eq('user_id', user.id);

    if (error) {
      logger.error('Error fetching archived chats:', error);
      return [];
    }

    return data.map(item => item.match_id);
  } catch (error) {
    logger.error('Error in getArchivedChats:', error);
    return [];
  }
}

/**
 * Get all archived group IDs for current user
 */
export async function getArchivedGroups(): Promise<string[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('archived_groups')
      .select('group_id')
      .eq('user_id', user.id);

    if (error) {
      logger.error('Error fetching archived groups:', error);
      return [];
    }

    return data.map(item => item.group_id);
  } catch (error) {
    logger.error('Error in getArchivedGroups:', error);
    return [];
  }
}
